import { BusinessObjectBase } from './base';
import { timerSchema } from '@schemas/business-objects/timer';
import type { Tenant } from './tenant';
import type { Token } from './token';
import type { Id, TimerStatus } from '../base-types';
import type { ConditionWatcher } from './condition-watcher';
import type { MessageListener } from './message-listener';
import type { PendingStateUpdate } from './pending-state-update';

export interface TimerData {
  readonly id: Id;
  readonly boundaryEventId: string;
  readonly interrupting?: boolean;
  readonly fireAt?: Date;
  readonly repeating?: boolean;
  readonly repeatLimit?: number;
  readonly fireCount?: number;
  readonly status?: TimerStatus;
  readonly tokenId: Id;
  readonly token?: Token;
  readonly tenantId: Id;
  readonly tenant?: Tenant;
  readonly createdAt?: Date;
  readonly updatedAt?: Date;
}

export interface Timer extends Readonly<TimerData> {}
type TimerCreateWithChildrenData = Partial<TimerData>;

export class Timer extends BusinessObjectBase {
  static readonly entityName = 'Timer' as const;
  static readonly plural = 'timers';
  static schema = timerSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<Timer[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new Timer(data));
  }

  static async findOne(filter?: any): Promise<Timer | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new Timer(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<Timer> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new Timer(data);
  }

  static async create(data: Omit<TimerData, 'id'>): Promise<Timer> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new Timer(result);
  }

  static async createMany(data: Omit<TimerData, 'id'>[]): Promise<Timer[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new Timer(item));
  }

  static async createWithChildren(
    data: TimerCreateWithChildrenData,
  ): Promise<Timer> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new Timer(result);
  }

  static async createManyWithChildren(
    data: TimerCreateWithChildrenData[],
  ): Promise<Timer[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new Timer(item));
  }

  static async updateById(id: Id, updates: Partial<TimerData>): Promise<Timer> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new Timer(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  async fire(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${Timer.plural}/${(this as any).id}/fire`;
    return BusinessObjectBase.post<any>(url, {});
  }
}
